import { inject, Injectable, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { TranslocoService } from '@jsverse/transloco';

import { AppLocalStorageService } from 'src/app/features/shared/storage/services/local-storage-services/app-local-storage.service';

export type TAppLang = 'nl' | 'en';

const AVAILABLE: TAppLang[] = ['nl', 'en'];
// Koppelt de UI-taal aan de Moment-datumlocale (MomentDateAdapter).
const DATE_LOCALES: Record<TAppLang, string> = { nl: 'nl', en: 'en-gb' };

@Injectable({ providedIn: 'root' })
export class LanguageService {
    private readonly __transloco = inject(TranslocoService);
    private readonly __dateAdapter = inject(DateAdapter);
    // Taalkeuze leeft in de geconsolideerde user-boom (tak i18n.lang) en wordt gesynct.
    private readonly __store = inject(AppLocalStorageService);

    private readonly __current$$ = signal<TAppLang>(this.__resolveInitialLang());
    /** Reactieve actieve taal (voor templates die op taalwissel moeten herrenderen). */
    readonly Current$$ = this.__current$$.asReadonly();

    get Available(): readonly TAppLang[] { return AVAILABLE; }

    // Past de starttaal toe: opgeslagen voorkeur → browsertaal → Transloco-default.
    init(): void {
        this.setLanguage(this.__resolveInitialLang());
    }

    get current(): TAppLang {
        return this.__current$$();
    }

    setLanguage(lang: TAppLang): void {
        // Catalogus expliciet inladen: de imperatieve translate() (fout-funnel + validatie)
        // leest synchroon uit de cache. Zonder deze load valt een net-gewisselde taal terug
        // op de fallback-taal omdat geen enkele Transloco-pipe de load triggert.
        this.__transloco.load(lang).subscribe();
        this.__transloco.setActiveLang(lang);
        this.__dateAdapter.setLocale(DATE_LOCALES[lang] ?? lang);
        this.__current$$.set(lang);
        this.__store.appData$$.i18n.lang.set(lang);
    }

    // Opgeslagen voorkeur wint; anders de eerste ondersteunde browsertaal; anders default.
    private __resolveInitialLang(): TAppLang {
        const _stored = this.__storedLang();
        if (_stored && AVAILABLE.includes(_stored)) return _stored;
        return this.__browserLang() ?? (this.__transloco.getDefaultLang() as TAppLang);
    }

    private __browserLang(): TAppLang | null {
        if (typeof navigator === 'undefined') return null;
        const _candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
        for (const _candidate of _candidates) {
            const _code = _candidate?.slice(0, 2).toLowerCase() as TAppLang;
            if (AVAILABLE.includes(_code)) return _code;
        }
        return null;
    }

    private __storedLang(): TAppLang | null {
        return (this.__store.appData$$.i18n.lang() as TAppLang | undefined) ?? null;
    }
}
